/**
 * CollectionContent.js
 *
 * Content section for collection cards.
 * Displays collection-specific information like item count, category breakdown, location.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryIcon } from '../../../utils/categoryUtils';
import Icon from '../../shared/Icon';

/**
 * Category breakdown display
 */
const CategoryBreakdown = ({ breakdown }) => {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const entries = Object.entries(breakdown).slice(0, 4);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
      {entries.map(([category, count]) => (
        <span key={category} className="flex items-center">
          <CategoryIcon category={category} className="w-3.5 h-3.5 mr-1" />
          <span>{count}</span>
        </span>
      ))}
    </div>
  );
};

/**
 * Main CollectionContent Component
 *
 * Props:
 * @param {Object} data - Normalized card data
 */
const CollectionContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const { title, description, typeData } = data;
  const {
    itemCount = 0,
    categoryBreakdown,
    primaryLocation,
    visibility
  } = typeData || {};

  const visibilityIconName = visibility === 'private' ? 'lock' : visibility === 'friends' ? 'userGroup' : 'globe';
  const visibilityTitle = visibility === 'private' ? t('collectionContent.visibility.private') : visibility === 'friends' ? t('collectionContent.visibility.friends') : t('collectionContent.visibility.public');

  return (
    <div className="flex-grow">
      {/* Title + visibility */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1 flex items-center gap-1.5">
        <span className="truncate">{title}</span>
        <span className="flex-shrink-0 text-xs text-content-muted" title={visibilityTitle}>
          <Icon name={visibilityIconName} className="w-3.5 h-3.5" />
        </span>
      </h3>

      {/* Collection stats */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-3">
        {itemCount > 0 && (
          <span className="flex items-center">
            <Icon name="folder" className="w-3.5 h-3.5 mr-1" />
            {t('collectionContent.itemCount', { count: itemCount })}
          </span>
        )}
        {primaryLocation && (
          <span className="flex items-center">
            <Icon name="mapPin" className="w-3.5 h-3.5 mr-1" />
            <span>{primaryLocation}</span>
          </span>
        )}
      </div>

      {/* Category breakdown */}
      <CategoryBreakdown breakdown={categoryBreakdown} />

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(CollectionContent);
